import * as Track from '@axon-enterprise/track';
import { Box, Button, CircularProgress, Divider, TextField, Typography } from '@mui/material';
import React, { useMemo, useRef, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';

import {
  TEMP_MX_PROPERTIES,
  mixpanelService,
  TEMP_MX_EVENTS,
} from "../common/tracking/MixpanelService";
import { RedirectPage } from '../components';
import LanguageMenuWrapper from '../components/LanguageMenuWrapper';
import ReCaptcha, { ReCaptchaRef } from '../components/ReCaptcha';
import { useClientSideSerial } from '../hooks/useClientSideSerial';
import { useSupportPhoneDisplay } from '../utils';
import { isValidSerialFormat } from '../utils/clientSideSerial/serialToModel';
import { getAppConfig } from '../utils/config';
import {
  AXON_LOGO_PATH,
  ERROR,
  REACHABLE,
  SOURCE_MODEL_RESOLVE,
  SUCCESS,
  getSupportMessage,
  SUPPORT_PHONE_TEL,
  UNREACHABLE,
} from "../utils/constants";
import { checkFususDevice } from '../utils/deviceCheck';
import { logger } from '../utils/logger';
import { ModelResolveError, resolveDeviceModel } from '../utils/modelResolve';

/**
 * LookupPage - Unified serial entry page for all device setup flows
 *
 * Route-aware behavior:
 * - /v2 route: Always uses client-side serial (no reCAPTCHA, pattern matching)
 * - /v1 route: Uses config to determine flow (backend API or client-side serial)
 *
 * Client-side serial flow:
 * - No reCAPTCHA required (client-side only)
 * - Uses client-side pattern matching to derive model
 * - Shows dropdown if serial unrecognized
 * - Navigates to appropriate guide route
 *
 * Standard flow:
 * - Shows reCAPTCHA (protects backend)
 * - Uses backend API for model resolution
 * - Navigates to appropriate guide route
 */
const LookupPage: React.FC = () => {
  const location = useLocation();
  const config = getAppConfig();

  // Detect which route we're on
  const isV2Route = location.pathname.startsWith('/v2');
  const guideRoute = isV2Route ? '/v2/guide' : '/guide';

  // Check for serial in query parameters (QR code flow)
  const queryParams = new URLSearchParams(location.search);
  const urlSerial = queryParams.get('serial');

  return <UnifiedLookupPage urlSerial={urlSerial} config={config} guideRoute={guideRoute} isV2Route={isV2Route} />;
};

/**
 * Unified lookup page component - handles both client-side serial resolution and standard flows
 *
 * Route-based behavior:
 * - /v2 route: Always uses client-side serial (no reCAPTCHA, pattern matching)
 * - /v1 route: Always uses backend API (reCAPTCHA, SKU resolution)
 *
 * Client-side serial flow: Pattern-based derivation, no backend calls, no reCAPTCHA
 * Standard flow: Backend API, reCAPTCHA required, device connectivity check
 */
interface UnifiedLookupPageProps {
  urlSerial: string | null;
  config: ReturnType<typeof getAppConfig>;
  guideRoute: string;
  isV2Route: boolean;
}

const UnifiedLookupPage: React.FC<UnifiedLookupPageProps> = ({ urlSerial, config, guideRoute, isV2Route }) => {
  // Shared state for both flows
  const [serial, setSerial] = useState(urlSerial || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const supportPhoneDisplay = useSupportPhoneDisplay();

  // Route determines flow (no config needed):
  // - V2 route: client-side serial
  // - V1 route: backend API
  const isClientSideSerialEnabled = isV2Route;

  // Client-side serial hook (only used if enabled)
  const { handleClientSideSerial } = useClientSideSerial();

  // Standard flow state (only used if OS v2 disabled)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [deviceUnreachable, setDeviceUnreachable] = useState(false);
  const recaptchaRef = useRef<ReCaptchaRef>(null);

  const isStandardFlow = !isClientSideSerialEnabled && !recaptchaToken;

  // V2 route: Validate serial format meets Axon hardware spec requirements
  // Must pass basic format validation before enabling Next button
  const isValidFormat = useMemo(() => {
    if (!isV2Route) return true; // V1 route: no client-side validation
    return isValidSerialFormat(serial);
  }, [serial, isV2Route]);

  const handleSerialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSerial(e.target.value);
    if (error) {
      setError('');
    }
  };

  const handleRecaptchaVerify = (token: string) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!serial.trim()) {
      setError(__('Please enter a serial number'));
      return;
    }

    // Client-side serial flow: No reCAPTCHA required (pattern-based resolution)
    // Standard flow: Require reCAPTCHA (protects backend)
    if (isStandardFlow) {
      setError(__('Please complete the reCAPTCHA verification'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (isClientSideSerialEnabled) {
        // ========================================
        // Client-Side Serial Flow: Pattern-based derivation
        // ========================================
        await handleClientSideSerial(serial);
      } else {
        // ========================================
        // Standard Flow: Backend API (UNCHANGED)
        // ========================================
        await handleStandardFlow();
      }
    } catch (err) {
      const error = err as Error;
      setError(error.message || __('An error occurred. Please try again.'));

      // Reset reCAPTCHA on error (only for standard flow)
      if (!isClientSideSerialEnabled && recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Standard Flow: Backend API model resolution (UNCHANGED)
   *
   * This is the existing production flow. All client-side serial resolution code
   * has been extracted to separate modules to avoid affecting this logic.
   */
  const handleStandardFlow = async () => {
    try {
      const sessionInfo = await resolveDeviceModel(serial, recaptchaToken!);

      // Log successful session creation for debugging
      logger.info('Session created and stored:', {
        sessionId: sessionInfo.session_id,
        deviceId: sessionInfo.data.device_id,
        model: sessionInfo.data.model,
        organizationId: sessionInfo.data.organization_id,
      });

      // Register durable context for all subsequent events
      mixpanelService.setMetadata({
        [TEMP_MX_PROPERTIES.ApplianceSerialNumber]: serial,
        [TEMP_MX_PROPERTIES.DeviceID]: sessionInfo.data.device_id,
        [Track.MxPropertyName.Model]: sessionInfo.data.model,
        [TEMP_MX_PROPERTIES.ApplianceOrganizationID]: sessionInfo.data.organization_id,
        [Track.MxPropertyName.Source]: SOURCE_MODEL_RESOLVE,
      });
      
      // Track successful lookup with session information
      mixpanelService.track(Track.MxEventName.SearchByDeviceSerialNumber,  {
        [Track.MxPropertyName.Status]: SUCCESS,
      });

      // Check if device is reachable
      const deviceResult = await checkFususDevice(serial);
      
      if (deviceResult.url) {
        // Device is reachable, redirect to it
        setRedirectUrl(deviceResult.url);
        setRedirecting(true);
        
        // Track successful connection
        mixpanelService.track(TEMP_MX_EVENTS.RedirectAttempted, {
          [Track.MxPropertyName.Status]: REACHABLE,
          [TEMP_MX_PROPERTIES.ConnectionMethod]: deviceResult.status,
          [Track.MxPropertyName.URL]: deviceResult.url,
        });
      } else {
        // Device is unreachable, show fallback page
        setDeviceUnreachable(true);
        
        // Track unreachable device
        mixpanelService.track(TEMP_MX_EVENTS.RedirectAttempted, {
          [Track.MxPropertyName.Status]: UNREACHABLE,
          [TEMP_MX_PROPERTIES.ApplianceSerialNumber]: serial,
        });
      }
      
    } catch (err) {
      const error = err as ModelResolveError;
      setError(error.message || __('An error occurred while looking up your device. Please try again.'));
      
      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
      
      // Track error
      mixpanelService.track(Track.MxEventName.SearchByDeviceSerialNumber, {
        [TEMP_MX_PROPERTIES.ApplianceSerialNumber]: serial,
        [Track.MxPropertyName.Status]: ERROR,
        [Track.MxPropertyName.ErrorReason]: error.message || 'unknown',
      });
    } finally {
      setLoading(false);
    }
  };
  if (redirecting && redirectUrl) {
    return <RedirectPage serial={serial} redirectUrl={redirectUrl} />;
  }

  if (deviceUnreachable) {
    return <Redirect to={guideRoute} />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        // Mobile-first responsive design
        padding: { xs: 2, sm: 3, md: 4 },
        '@media (max-width: 768px)': {
          padding: 1,
          minHeight: '100vh'
        }
      }}
    >
      {/* Mobile-optimized container */}
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 420, md: 480 },
          margin: '0 auto',
          bgcolor: 'white',
          borderRadius: { xs: 0, sm: 2 },
          boxShadow: { xs: 'none', sm: '0 4px 6px rgba(0, 0, 0, 0.1)' },
          overflow: 'hidden',
          minHeight: { xs: 'auto', sm: 'auto' },
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header Section with Logo */}
        <Box
          sx={{
            bgcolor: 'white',
            p: { xs: 2, sm: 3 },
            pb: { xs: 1.5, sm: 2 },
            display: 'flex',
            justifyContent: 'center',
            borderBottom: { xs: 'none', sm: '1px solid #e0e0e0' }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '175.89px',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: '7px'
            }}
          >
            <Box
              component="img"
              src={AXON_LOGO_PATH}
              alt="Axon Logo"
              sx={{
                height: { xs: 28, sm: 32 },
                width: 'auto'
              }}
            />
          </Box>
        </Box>

        {/* Mobile-optimized line separator */}
        <Divider 
          sx={{ 
            borderColor: '#e0e0e0',
            borderWidth: 1,
            display: { xs: 'block', sm: 'none' }
          }} 
        />

        {/* Main Content Section */}
        <Box
          sx={{
            bgcolor: '#fafafa',
            p: { xs: 2, sm: 3 },
            flex: 1,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Welcome Text - Mobile optimized */}
          <Typography 
            variant="h3"
            component="h1" 
            sx={{ 
              mb: { xs: 1.5, sm: 2 },
              textAlign: 'center'
            }}
          >
            {__('Welcome to the Core connection wizard.')}
          </Typography>
          
          <Typography 
            variant="h6"
            sx={{ 
              mb: { xs: 2, sm: 3 },
              textAlign: 'left'
            }}
          >
            {__('Before we get started we need to know your device\'s serial number. It should be located on a black sticker on the devices box underneath the barcode.')}
          </Typography>

          {/* Example Section */}
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <Divider 
              sx={{ 
                borderColor: '#e0e0e0',
                borderWidth: 0.5,
                mb: 2
              }} 
            />
            <Typography 
              variant="overline" 
              sx={{ 
                fontWeight: 600,
                mb: 2,
                display: 'block'
              }}
            >
              {__('Example:')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 2
              }}
            >
              <Box
                component="img"
                src="/assets/images/example-label.png"
                alt={__('Serial number example')}
                sx={{
                  borderRadius: 1,
                  border: '1px solid #ddd',
                  width: { xs: '160px', sm: '180px' },
                  height: 'auto',
                  maxWidth: '100%'
                }}
              />
            </Box>
          </Box>

          {/* Form - Mobile optimized */}
          <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1 }}>
            {/* Serial Number Label */}
            <Typography 
              variant="overline" 
              component="label" 
              htmlFor="serial-input"
              sx={{ 
                color: error ? 'error.main' : 'text.primary',
                fontWeight: 600,
                mb: 1,
                display: 'block'
              }}
            >
              {__('Serial Number')}
            </Typography>
            
            {/* Serial Number Input - Mobile friendly */}
            <TextField
              fullWidth
              value={serial}
              onChange={handleSerialChange}
              placeholder={__('Enter serial number')}
              disabled={loading}
              error={!!error}
              variant="outlined"
              size="medium"
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  bgcolor: error ? 'error.lighter' : 'background.paper',
                  borderRadius: 1,
                  minHeight: { xs: '48px', sm: '40px' }, // Touch-friendly height
                  '& fieldset': {
                    borderColor: error ? 'error.main' : 'divider'
                  },
                  '&:hover fieldset': {
                    borderColor: error ? 'error.main' : 'text.secondary'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: error ? 'error.main' : 'primary.main',
                    borderWidth: 2
                  }
                },
                '& .MuiOutlinedInput-input': {
                  padding: { xs: '12px 14px', sm: '8.5px 14px' }
                }
              }}
            />

            {/* Error Message - Mobile friendly */}
            {error && (
              <Typography
                variant="caption"
                sx={{
                  color: 'error.main',
                  mb: 2,
                  display: 'block'
                }}
              >
                {error}
              </Typography>
            )}

            {/* V2 Format Validation Helper - Only show when serial entered but invalid */}
            {isV2Route && serial && !isValidFormat && !error && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  mb: 2,
                  display: 'block'
                }}
              >
                {__('Serial must be 9+ alphanumeric characters (e.g., F16A4W37A)')}
              </Typography>
            )}

            {/* reCAPTCHA - Only shown on V1 route (backend API flow) */}
            {!isClientSideSerialEnabled && config.googleRecaptchaSiteKey && (
              <Box sx={{
                mb: { xs: 2, sm: 2 },
                display: 'flex',
                justifyContent: 'center',
                '& > div': {
                  transform: { xs: 'scale(0.9)', sm: 'scale(1)' },
                  transformOrigin: 'center'
                }
              }}>
                <ReCaptcha
                  ref={recaptchaRef}
                  siteKey={config.googleRecaptchaSiteKey}
                  onVerify={handleRecaptchaVerify}
                  size="normal"
                  theme="light"
                  disabled={loading}
                />
              </Box>
            )}

            {/* Next Button - Mobile optimized */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !serial || !isValidFormat || isStandardFlow}
              aria-label={__('Next')}
              sx={{
                py: { xs: 1.5, sm: 1.25 },
                borderRadius: 1,
                mb: { xs: 2, sm: 3 },
                minHeight: { xs: '48px', sm: '40px' }, // Touch-friendly height
                '&:disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled'
                }
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : __('Next')}
            </Button>

            {/* Support Info - Mobile optimized */}
            <Typography 
              variant="caption" 
              sx={{ 
                textAlign: 'center',
                mt: 'auto',
                pt: { xs: 1, sm: 0 },
                display: 'block'
              }}
            >
              {getSupportMessage()}{' '}
              <Box 
                component="span" 
                sx={{ 
                  color: 'primary.main',
                  fontWeight: 500,
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
                onClick={() => window.open(SUPPORT_PHONE_TEL)}
              >
                {supportPhoneDisplay}
              </Box>
              .
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* Language Menu */}
      {config.enableI18n && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 3,
            pb: 2,
          }}
        >
          <LanguageMenuWrapper />
        </Box>
      )}
    </Box>
  );
};

export default LookupPage;
